import { createSelector } from "reselect";

import { CommandPaletteRecentAction } from "components/CommandPalette/types";

import type { CommandPaletteRecentActionsStore } from "./types";

const STALE_THRESHOLD_DAYS = 30;
const TOP_ACTIONS_COUNT = 5;
const RECENCY_BONUS_DAYS = 5;
const VELOCITY_WINDOW_DAYS = 7;

// Scoring weights
const VELOCITY_WEIGHT = 10; // Weight for uses per day in velocity window
const TOTAL_COUNT_WEIGHT = 0.3; // Weight for total usage count

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const selectActionUsage = (state: CommandPaletteRecentActionsStore) => state.actionUsage;

// Count uses within the velocity window (last N days)
const countRecentUses = (timestamps: number[], now: number, windowDays: number): number => {
  const windowStart = now - windowDays * MS_PER_DAY;
  return timestamps.filter((ts) => ts >= windowStart).length;
};

// Calculate score with velocity detection for "quick growing" items
const calculateScore = (action: CommandPaletteRecentAction, now: number) => {
  const { count, lastUsed, timestamps = [] } = action.usage;

  // Recency bonus (0-5 based on days since last use)
  const daysSinceUsed = (now - lastUsed) / MS_PER_DAY;
  const recencyBonus = Math.max(0, RECENCY_BONUS_DAYS - Math.floor(daysSinceUsed));

  // Velocity: uses per day in the recent window
  const recentUses = countRecentUses(timestamps, now, VELOCITY_WINDOW_DAYS);
  const velocity = recentUses / VELOCITY_WINDOW_DAYS;

  // Combined score: velocity (weighted) + total count (weighted) + recency bonus
  return velocity * VELOCITY_WEIGHT + count * TOTAL_COUNT_WEIGHT + recencyBonus;
};

// Get top N scored recent actions (not stale)
export const selectTopRecentActions = createSelector([selectActionUsage], (actionUsage) => {
  const now = Date.now();
  const staleThresholdMs = STALE_THRESHOLD_DAYS * MS_PER_DAY;

  // Filter out stale actions
  const recentActions = Object.values(actionUsage).filter(
    (action) => now - action.usage.lastUsed < staleThresholdMs
  );

  const scored = recentActions.map((action) => ({
    action,
    score: calculateScore(action, now),
  }));

  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, TOP_ACTIONS_COUNT)
    .map((item) => item.action);
});
